import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { faListUl, faSearchPlus } from '@fortawesome/pro-regular-svg-icons';
import { faConveyorBeltAlt } from '@fortawesome/pro-solid-svg-icons';
import { select, Store } from '@ngrx/store';
import { openList } from 'app/list/actions/list.actions';
import { ShowcasePermissions } from 'app/permissions/models/permissions';
import { runSavedSearch } from 'app/saved-search/actions/saved-search.actions';
import { FeatureToggleService } from 'app/services/feature-toggle.service';
import { LocalizationService } from 'app/services/localization.service';
import { combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ConfigurationLoader } from 'shared/configuration-loader';
import { dateFormatFactory } from 'shared/date-format-factory';
import { IlsType } from 'shared/models/ilsType';
import { areEqualDeeply } from 'shared/utils/are-equal-deeply';
import { openBookmarksTab, openShowcasesTab } from 'user/actions/user-profile.actions';
import { MaxHeightPerfectScrollbar } from 'user/models/bookshelf';
import { UserPermission } from 'user/models/user';
import { doesUserHavePermission, getStaffUserName, getUserNickname } from 'user/reducers/user.reducer';
import {
  clearOpenedState,
  create, createFromIlsList,
  hideForm,
  loadIlsRemoteLists,
  openShowcase,
  resetCreateState,
  resetUpdateState,
  update
} from '../../actions/custom-showcase.actions';
import { CustomShowcaseCreatedFromType, CustomShowcaseForm, isDynamicShowcase } from '../../models/custom-showcase';
import { customShowcaseMetadataDefaults, customShowcaseMetadataSavedSearchDefaults } from '../../models/custom-showcase.defaults';
import {
  areIlsListsLoaded,
  CustomShowcaseState,
  getCreateState,
  getFormData,
  getIlsListOptions,
  getIsShowcaseOpened,
  getUpdateState,
} from '../../reducers/custom-showcase.reducer';
import { CustomShowcaseListSyncDialogComponent } from '../custom-showcase-list-sync-dialog/custom-showcase-list-sync-dialog.component';

@Component({
  selector: 'app-custom-showcase-form',
  templateUrl: './custom-showcase-form.component.html',
  styleUrls: ['./custom-showcase-form.component.scss'],
  providers: [
    {provide: MAT_DATE_FORMATS, useFactory: dateFormatFactory, deps: [LocalizationService]},
  ],
})
export class CustomShowcaseFormComponent implements OnInit, OnDestroy {
  @ViewChild('nameField') nameField: ElementRef;
  public customShowcaseForm: UntypedFormGroup;
  public isSaving: boolean;
  public hasError: boolean;
  public isEditMode = false;
  public polarisRecordSetNotFound = false;
  public showcase: CustomShowcaseForm;
  public showcasePermissions = ShowcasePermissions;
  public isAuthPatronFlagEnabled: boolean;
  public readonly createdFromType = CustomShowcaseCreatedFromType;
  public readonly nickname$ = this.store.pipe(select(getUserNickname));
  public readonly staffName$ = this.store.pipe(select(getStaffUserName));
  public readonly hasPublishShowcasePermission$ = this.store.select(doesUserHavePermission(UserPermission.SHOWCASES_PUBLISH));
  public readonly maxHeightPerfectScrollbar = MaxHeightPerfectScrollbar.BASE;
  public readonly showcaseIcon = faConveyorBeltAlt;
  public readonly searchIcon = faSearchPlus;
  public readonly listIcon = faListUl;
  private readonly subscription = new Subscription();
  private isSaveAndPublish = false;
  private isShowcaseOpened = false;
  private isUpdated = false;
  private ilsListsLoaded = false;
  public currentPublishStatus: boolean;
  public today = new Date();
  public isShowCaseScheduleFlagEnabled: boolean = false;
  public showPolarisRecordIdInput = false;
  public showSierraIlsDrpdown = false;
  public ilsListOptions: Array<{ id: string; name: string }> = [];
  public readonly showcaseType = CustomShowcaseCreatedFromType;

  @Inject(MAT_DATE_FORMATS) private dateFormats: any

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly store: Store<CustomShowcaseState>,
    private readonly cdr: ChangeDetectorRef,
    private readonly featureToggleService: FeatureToggleService,
    private readonly configLoader: ConfigurationLoader,
    private readonly modalService: NgbModal,
  ) {
    this.store.dispatch(resetCreateState());
    this.store.dispatch(resetUpdateState());
    this.customShowcaseForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(50)]],
      ilsListDropdownMenu: [null,[Validators.required]],
      ilsListIdInput: ['',[Validators.required]],
      metadata: this.formBuilder.group({
        sortOrder: [customShowcaseMetadataDefaults.sortOrder],
        displayOn: this.formBuilder.group(customShowcaseMetadataDefaults.displayOn),
        displayCreator: [customShowcaseMetadataDefaults.displayCreator],
        aboutShowcase: this.formBuilder.group({
          startDate: ['', [Validators.maxLength(50)]],
          endDate: ['', [Validators.maxLength(50)]],
          deleteExpired: [false],
          rapido: [false],
          type: this.formBuilder.group(customShowcaseMetadataDefaults.aboutShowcase.type),
          age: this.formBuilder.group(customShowcaseMetadataDefaults.aboutShowcase.age),
          format: this.formBuilder.group(customShowcaseMetadataDefaults.aboutShowcase.format),
        }),
      }),
    });
  }

  public ngOnInit(): void {
    this.isAuthPatronFlagEnabled = this.featureToggleService.getToggles()['DIS-30793_2024-04-27_auth_patron'];
    this.isShowCaseScheduleFlagEnabled = this.featureToggleService.getToggles()['DIS-31554_2024-08-30_schedule_showcase'];
    this.subscription.add(
      this.store.select(getFormData)
        .pipe(distinctUntilChanged(areEqualDeeply))
        .subscribe((formData) => {
          if (formData.showcase) {

            this.showcase = formData.showcase;

            const ilsListId = this.showcase?.createdFrom.ilsListId;
            const isFromIlsList = !!formData.isFromIlsList;

            this.isEditMode = !!this.showcase.id;

            const prev = this.showSierraIlsDrpdown;
            const isPolarisIls = this.configLoader.ilsType === IlsType.polaris;
            const isSierraIls = this.configLoader.ilsType === IlsType.sierra;

            this.showPolarisRecordIdInput = isPolarisIls && (isFromIlsList || !!ilsListId);

            this.showSierraIlsDrpdown = isSierraIls && (isFromIlsList || !!ilsListId);

            if (this.showSierraIlsDrpdown && !prev && !this.ilsListsLoaded) {
              this.store.dispatch(loadIlsRemoteLists());
            }

            if (this.isEditMode) {
              this.name.setValue(formData.showcase.name);
              if (isDynamicShowcase(this.showcase)) {
                this.metadata.removeControl('sortOrder');
              }
              this.metadata.setValue(formData.showcase.metadata);
              if (ilsListId) {
                this.setInitialValuesForListFormFields(ilsListId,isPolarisIls,isSierraIls);
              }
            } else {
              this.name.setValue(formData.showcase.createdFrom.name);
              if (isDynamicShowcase(this.showcase)) {
                this.metadata.setValue(customShowcaseMetadataSavedSearchDefaults);
              }
            }
          }
          this.turnOnValidationForIlsListsInputs();
          this.cdr.markForCheck();
        }),
    );

    this.subscription.add(
      this.store.select(getIlsListOptions).subscribe(opts => {
        this.ilsListOptions = opts ?? [];
        this.cdr.markForCheck();
      })
    );

    this.subscription.add(
      this.store.select(areIlsListsLoaded).subscribe(loaded => {
        this.ilsListsLoaded = loaded;
      })
    );

    this.subscription.add(
      combineLatest([
        this.store.select(getCreateState),
        this.store.select(getUpdateState),
      ])
        .pipe(distinctUntilChanged(areEqualDeeply))
        .subscribe(([createState, updateState]) => {
          this.isSaving = createState.loading || updateState.loading;
          this.hasError = !!(createState.errorCode || updateState.errorCode);
          this.polarisRecordSetNotFound = createState.errorCode === 404 || updateState.errorCode === 404;
          if (createState.id) {
            this.close();
            this.store.dispatch(clearOpenedState());
            this.store.dispatch(openShowcasesTab());
          }
          if (updateState.updated) {
            this.isUpdated = true;
          }
          if (updateState.updated && !this.isSaveAndPublish) {
            this.close();
            if (this.isShowcaseOpened) {
              this.store.dispatch(openShowcase({id: this.showcase.id}));
            }
          }
          this.cdr.markForCheck();
        }),
    );

    this.subscription.add(
      this.store.select(getIsShowcaseOpened)
        .subscribe((isShowcaseOpened) => {
          this.isShowcaseOpened = isShowcaseOpened;
        }),
    );
    this.onFocusSetup();
  }

  private onFocusSetup() {
    setTimeout(() => {
      this.nameField?.nativeElement.focus();
    });
  }

  private setInitialValuesForListFormFields(
    ilsListId: string | null,
    isPolarisIls: boolean,
    isSierraIls: boolean
  ) {
    if (isPolarisIls) {
      this.ilsListIdInput?.setValue(ilsListId ?? '', { emitEvent: false });
    } else if (isSierraIls) {
      this.ilsListDropdownMenu?.setValue(ilsListId ?? null, { emitEvent: false });
    }
  }


  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public save(isSaveAndPublish = false) {
    this.isSaveAndPublish = isSaveAndPublish;
    this.currentPublishStatus = this.showcase.published;
    this.name.markAsTouched();
    if (this.showPolarisRecordIdInput) this.ilsListIdInput?.markAsTouched();
    if (this.showSierraIlsDrpdown) this.ilsListDropdownMenu?.markAsTouched();
    if (!this.customShowcaseForm.valid || this.isSaving) {
      return;
    }

    if (this?.showcase?.createdFrom?.rapido) {
      this.customShowcaseForm.get('metadata.aboutShowcase.rapido').setValue(true);
    }

    const formValue = {...this.customShowcaseForm.value };
    this.isEditMode
      ? this.update(formValue, isSaveAndPublish, this.currentPublishStatus)
      : this.create(formValue, isSaveAndPublish, this.currentPublishStatus);
  }


  public cancel() {
    this.close();
    if (this.isUpdated && this.isShowcaseOpened) {
      this.store.dispatch(openShowcase({id: this.showcase.id}));
    }
  }

  get ilsListIdInput() {
    return this.customShowcaseForm.get('ilsListIdInput');
  }

  get ilsListDropdownMenu() {
    return this.customShowcaseForm.get('ilsListDropdownMenu');
  }

  public get name(): AbstractControl | null {
    return this.customShowcaseForm.get('name');
  }

  public get metadata(): UntypedFormGroup | null {
    return this.customShowcaseForm.get('metadata') as UntypedFormGroup;
  }

  public get aboutShowcase(): UntypedFormGroup | null {
    return this.metadata.get('aboutShowcase') as UntypedFormGroup;
  }

  public onShowcaseTypeClick(): void {
    isDynamicShowcase(this.showcase) ? this.runSavedSearch() : this.openList();
  }

  private runSavedSearch(): void {
    this.store.dispatch(runSavedSearch({savedSearchId: this.showcase.createdFrom.id}));
  }

  private openList(): void {
    this.store.dispatch(openBookmarksTab());
    this.store.dispatch(openList({listId: this.showcase.createdFrom.id}));
  }

  private create(formValue: any, isSaveAndPublish: boolean = false, currentPublishStatus: boolean = false) {
    const { ilsListIdInput, ...rest } = formValue;

    const createdFrom = { ...this.showcase.createdFrom };


    if (this.showPolarisRecordIdInput) {
      (createdFrom as any).ilsListId = ilsListIdInput;
    }

    if (this.showSierraIlsDrpdown) {
      const sel = this.getSelectedSierra();
      (createdFrom as any).ilsListId = sel.id;
      (createdFrom as any).ilsListName = sel.name;
    }

    const payload = {
      ...rest,
      createdFrom,
    };

    if (this.showPolarisRecordIdInput || this.showSierraIlsDrpdown) {
      this.store.dispatch(createFromIlsList({form: payload, isSaveAndPublish, currentPublishStatus}));
    } else {
      this.store.dispatch(create({form: payload, isSaveAndPublish, currentPublishStatus}));
    }

  }

  private update(formValue: any, isSaveAndPublish: boolean = false, currentPublishStatus: boolean) {

    const { ilsListIdInput, ilsListDropdownMenu, ...rest } = formValue;
    const updatedFields: any = { ...rest };

    if (this.showPolarisRecordIdInput || this.showSierraIlsDrpdown) {
      const createdFrom: any = { ...this.showcase.createdFrom };
      if (this.showPolarisRecordIdInput) {
       createdFrom.ilsListId = (this.ilsListIdInput?.value ?? '').toString().trim();
      } else {
        const sierraList = this.getSelectedSierra();
        if (sierraList.id) {
          createdFrom.ilsListId = sierraList.id;
          createdFrom.ilsListName = sierraList.name ?? null;
        }
      }
      updatedFields.createdFrom = createdFrom;
    }

    this.store.dispatch(update({
      updatedFields: updatedFields,
      id: this.showcase.id,
      isSaveAndPublish,
      currentPublishStatus
    }));
  }

  private close() {
    this.store.dispatch(resetCreateState());
    this.store.dispatch(resetUpdateState());
    this.store.dispatch(hideForm());
  }

  public onSyncWithIls(): void {
    const modalRef = this.modalService.open(CustomShowcaseListSyncDialogComponent, {
      centered: true,
      windowClass: 'inspire-custom-modal',
    });
    modalRef.componentInstance.showcase = this.showcase;
  }

  private turnOnValidationForIlsListsInputs() {
    const polarisRecordIdInput = this.customShowcaseForm.get('ilsListIdInput')!;
    const sierraListInput = this.customShowcaseForm.get('ilsListDropdownMenu')!;

    if (this.showPolarisRecordIdInput) {
      if (this.isEditMode) {
        polarisRecordIdInput.disable({ emitEvent: false });
      } else {
        polarisRecordIdInput.enable({ emitEvent: false });
      }
      polarisRecordIdInput.setValidators([Validators.required]);

      sierraListInput.disable({ emitEvent: false });
      sierraListInput.clearValidators();
      sierraListInput.reset(null, { emitEvent: false });
    } else if (this.showSierraIlsDrpdown) {
      if (this.isEditMode) {
        sierraListInput.disable({ emitEvent: false });
      } else {
        sierraListInput.enable({ emitEvent: false });
      }
      sierraListInput.setValidators([Validators.required]);

      polarisRecordIdInput.disable({ emitEvent: false });
      polarisRecordIdInput.clearValidators();
      polarisRecordIdInput.reset('', { emitEvent: false });
    } else {
      polarisRecordIdInput.disable({ emitEvent: false });
      polarisRecordIdInput.clearValidators();

      sierraListInput.disable({ emitEvent: false });
      sierraListInput.clearValidators();
    }

    polarisRecordIdInput.updateValueAndValidity({ emitEvent: false });
    sierraListInput.updateValueAndValidity({ emitEvent: false });
  }

  private getSelectedSierra(): { id: string | null; name: string | null } {
    const selectedId = this.ilsListDropdownMenu?.value as string | null;
    if (!selectedId) return { id: null, name: null };
    const found = this.ilsListOptions?.find(o => o.id === selectedId);
    return { id: selectedId, name: found?.name ?? null };
  }

}
